import React, { useState, useRef, useEffect, useMemo } from "react";
import { Button, Badge, Form, InputGroup, ListGroup } from "react-bootstrap";
import { X, Plus } from "react-bootstrap-icons";

const TagsInput = ({ tags = [], onChange, disabled = false, maxTags = 5, existingTags = [] }) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  
  const sortedTags = useMemo(() => [...(tags || [])].sort(), [tags]);

  const filteredSuggestions = useMemo(() => {
    return (existingTags || [])
      .filter(tag => 
        (!inputValue.trim() || tag.toLowerCase().includes(inputValue.toLowerCase())) && 
        !(tags || []).includes(tag)
      )
      .slice(0, 5);
  }, [inputValue, existingTags, tags]);

  const addTag = (tagToAdd = inputValue.trim()) => {
    if (!tagToAdd) return;
    
    // Split by comma and process multiple tags
    const newTags = tagToAdd
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag && !(tags || []).includes(tag));
    
    // Only add tags if we don't exceed maxTags
    const tagsToAdd = newTags.slice(0, maxTags - (tags || []).length);
    
    if (tagsToAdd.length > 0) {
      onChange([...(tags || []), ...tagsToAdd]);
    }
    
    setInputValue("");
    setShowSuggestions(false);
  };

  const removeTag = (tagToRemove) => {
    onChange((tags || []).filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    addTag(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div className="tags-input" style={{ position: "relative" }}>
      <Form.Group>
        <Form.Label>Tags ({(tags || []).length}/{maxTags})</Form.Label>
        <InputGroup>
          <Form.Control
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => setShowSuggestions(filteredSuggestions.length > 0)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Add tags (separate with commas)..."
            disabled={disabled || (tags || []).length >= maxTags}
            className="input-control"
          />
          <Button
            variant="primary"
            onClick={() => addTag()}
            disabled={disabled || !inputValue.trim() || (tags || []).length >= maxTags}
            style={{ backgroundColor: "#00008F", borderColor: "#00008F" }}
          >
            <Plus size={16} />
          </Button>
        </InputGroup>
        
        {showSuggestions && (
          <ListGroup 
            style={{ 
              position: "absolute", 
              zIndex: 1000, 
              width: "100%", 
              maxHeight: "150px", 
              overflowY: "auto",
              marginTop: "2px"
            }}
          >
            {filteredSuggestions.map((suggestion, index) => (
              <ListGroup.Item
                key={index}
                action
                onMouseDown={() => selectSuggestion(suggestion)}
                style={{ 
                  cursor: "pointer",
                  padding: "0.5rem",
                  fontSize: "0.9rem"
                }}
              >
                {suggestion}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Form.Group>
      
      <div className="tags-display mt-2">
        {sortedTags.map((tag, index) => (
          <Badge
            key={index}
            pill
            className="me-2 mb-2 tag-badge"
            style={{ 
              backgroundColor: "#00008F", 
              color: "white",
              fontSize: "0.9em",
              padding: "0.5em 0.8em"
            }}
          >
            {tag}
            {!disabled && (
              <X
                size={14}
                className="ms-1"
                style={{ cursor: "pointer" }}
                onClick={() => removeTag(tag)}
              />
            )}
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default TagsInput;
